// src/components/SiteHeader.jsx
import React, { useEffect, useRef, useState, Suspense } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Menu, ChevronDown } from 'lucide-react';
import { withLang } from '../utils/withLang';
import { createPortal } from 'react-dom';

// логотипы по языкам (как у вас в страницах)
import logo_rus from '../assets/logo_rus.png';
import logo_eng from '../assets/logo_eng.png';
import logo_esp from '../assets/logo_esp.png';
import logo_zh  from '../assets/logo_zh.png';

import logo_rus_dark from '../assets/logo_rus_dark.png';
import logo_eng_dark from '../assets/logo_eng_dark.png';
import logo_esp_dark from '../assets/logo_esp_dark.png';
import logo_zh_dark  from '../assets/logo_zh_dark.png';

import bicon from '../assets/BIcon_s.png';
import licon from '../assets/LIcon_s.png';
import micon from '../assets/MIcon_s.png';
import cicon from '../assets/CIcon_s.png';

const LanguageSwitcher = React.lazy(() => import('./LanguageSwitcher'));


const LOGOS = { ru: logo_rus, en: logo_eng, es: logo_esp, zh: logo_zh };
const LOGOS_DARK = { ru: logo_rus_dark, en: logo_eng_dark, es: logo_esp_dark, zh: logo_zh_dark };

/**
 * props:
 * - pageLinks: [{ label, href (relative, типа "#pricing" или абсолютный "/route") }]
 * - cta: { label, to } (опционально)
 * - className (опц.)
 * - sticky (bool, по умолчанию true)
 */
export default function SiteHeader({ pageLinks = [], cta, bttns, className = '', sticky = true, mode = '' }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();

  const currentLang = (i18n.resolvedLanguage || i18n.language || lang || 'ru').slice(0,2);
  const logo = LOGOS[currentLang] || logo_rus;
  const logoDark = LOGOS_DARK[currentLang] || logo_rus

  const [isOpen, setIsOpen] = useState(false);                  // мобильный сайдбар
  const [isProductOpen, setIsProductOpen] = useState(false);    // дропдаун «О продуктах» (desktop)
  const [isProductOpenMobile, setIsProductOpenMobile] = useState(false); // аккордеон в мобильном

  const ddRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => { if (ddRef.current && !ddRef.current.contains(e.target)) setIsProductOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') { setIsOpen(false); setIsProductOpen(false); } };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onEsc); };
  }, []);

  const go = (to) => { navigate(to); setIsOpen(false); setIsProductOpen(false); };

  // «О продуктах» (как в вашем Header.jsx), но автоматически с языковым префиксом
  const productItems = [
    { label: t('$register', { defaultValue: 'Реестр готовых решений' }), to: withLang('/registry', currentLang), 
    icon: <img src={cicon} className='w-[30px] h-[30px]' /> },
    { label: t('$training', { defaultValue: 'Обучение лучшим практикам' }), to: withLang('/courses', currentLang), 
    icon: <img src={licon} className='w-[30px] h-[30px]' /> },
    { label: t('$marketplace', { defaultValue: 'Маркетплейс готовых решений' }), to: withLang('/marketplace', currentLang), 
    icon: <img src={micon} className='w-[30px] h-[30px]' />},
    { label: t('$BookOfBestPractices', { defaultValue: 'Книга лучших практик' }), to: withLang('/book', currentLang),
    icon: <img src={bicon} className='w-[30px] h-[30px]' />},
  ];

  const headerClassesDefault = [
    "sticky top-0 z-40",
    "flex flex-row justify-between sticky top-0 h-[70px] md:h-[100px] px-[20px] py-[10px] z-50 border-b border-gray-100",
    "bg-[#F6F8FC] backdrop-blur supports-[backdrop-filter]:bg-white/70"
  ].join(' ');

  const headerClassesLight = [
    sticky ? 'sticky top-0 z-40' : '',
    'border-b',
    'bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70',
    'border-gray-100',
    'h-[70px] md:h-[100px]',
    'px-[20px] py-[10px]',
    'flex flex-row justify-between',
  ].join(' ');

  const headerClassesDark = [
    "flex flex-row justify-between sticky top-0 h-[70px] md:h-[100px] px-[20px] py-[10px] z-50 border-b border-white/10 bg-[rgba(10,12,20,0.46)] backdrop-blur-md",
  ].join(' ');

  const headerButtonCommon = "inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-semibold hover:opacity-90 transition whitespace-nowrap shrink-0";
  const headerButttonDefault1 = [headerButtonCommon, "bg-[#F1F5F7] text-black mr-auto"].join(' ');
  const headerButttonDefault2 = [headerButtonCommon, "bg-black text-white mr-auto"].join(' ');

  const headerButtonLight = [headerButtonCommon, "text-white bg-gradient-to-r from-purple-600 to-indigo-600 mr-auto"].join(' ');
  const headerButtonDark = [headerButtonCommon, "gap-2 rounded-full border border-white/10 bg-[linear-gradient(90deg,#7CFFCB,#7AA2FF,#FF9DCE)] font-extrabold text-[#061015] shadow-[0_8px_24px_rgba(0,0,0,0.35)] mr-auto"]
  const headerLinksLight = "hidden md:flex items-center gap-6 text-sm text-gray-800 flex-nowrap";
  const headerLinksDark = "hidden md:flex items-center gap-6 text-sm text-white-800 flex-nowrap";

  return (
    <header className={mode === 'default' ? headerClassesDefault : mode === 'dark' ? headerClassesDark : headerClassesLight}>
        {/* Лого */}
        <Link to={withLang('/', currentLang)} style={{height: '100%', position: 'relative', zIndex:'2000', minWidth: '120px'}}>
          <img src={mode === 'dark' ? logoDark : logo} style={{ height: '100%', width: 'auto' }} alt="logo" />
        </Link>

      <div className="flex">
        {/* Десктопное меню */}
        <nav className={mode === 'dark' ? headerLinksDark : headerLinksLight}>
          {/* Индивидуальные ссылки страницы */}
          {pageLinks.map((l) => (
            // если ссылка начинается с '#', оставляем как есть; если это роут — префиксуем языком
            l.href?.startsWith('#') ? (
              <a key={l.label} href={l.href} className="hover:text-indigo-600 whitespace-nowrap">{l.label}</a>
            ) : (
              <Link key={l.label} to={withLang(l.href, currentLang)} className="hover:text-indigo-600 whitespace-nowrap">
                {l.label}
              </Link>
            )
          ))}

          {/* Дропдаун «О продуктах» */}
          {mode === 'default' ? (
            <div>
              {productItems.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  className="rounded-lg px-3 py-2 hover:bg-gray-50"
                >
                  {p.label}
                </Link>
              ))}
            </div>
          ) : (
            <li className="relative list-none" ref={ddRef}>
            <button
              className="inline-flex items-center gap-1 hover:text-indigo-600 whitespace-nowrap"
              onClick={() => setIsProductOpen(v => !v)}
              aria-haspopup="menu"
              aria-expanded={isProductOpen}
            >
              {t('$AboutProduct', { defaultValue: 'О продуктах' })}
              <ChevronDown size={16} />
            </button>
            {isProductOpen && (
              <div className="absolute right-0 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow">
                <nav className="flex flex-col gap-2 text-black">
                  {productItems.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      onClick={() => setIsProductOpen(false)}
                      className="rounded-lg px-3 py-2 hover:bg-gray-50"
                    >
                      {p.label}
                    </Link>
                  ))}
                </nav>
              </div>
            )}
          </li>
          )}

          {/* CTA и язык (desktop) */}
          {mode === 'default' ? (
            <div className="hidden md:flex items-center gap-3">
                <Link
                  to={withLang(cta[0].to, currentLang)}
                  className={headerButttonDefault1}
                >
                {cta[0].label}
                </Link>
                <Link
                  to={withLang(cta[1].to, currentLang)}
                  className={headerButttonDefault2}
                >
                {cta[1].label}
                </Link>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-3">
            {cta?.map((b) => (
              <Link
                to={withLang(b.to, currentLang)}
                className={mode === 'dark' ? headerButtonDark : headerButtonLight}
              >
                {b.label}
              </Link>
            ) 
            )}
            {bttns?.map((b) => (
              <button
                onClick={b.handleOnClick}
                className={mode === 'dark' ? headerButtonDark : headerButtonLight}
              >
                {b.label}
              </button>
            )
            )}
            </div>
          )}
          <Suspense fallback={<div className="text-xs text-gray-500">…</div>}>
            <LanguageSwitcher className={'text-[0.875rem] w-[180px] text-black bg-white rounded-2xl border-gray'}/>
          </Suspense>
        </nav>

        {/* Мобильные элементы */}
        <button className="md:hidden p-2" onClick={() => setIsOpen(true)} aria-label="Open menu">
          <Menu size={28} />
        </button>

        {/* Сайдбар (mobile) */}
        {isOpen && createPortal(
            <aside
              className="fixed right-0 top-0 h-full w-full bg-white text-black p-4 flex flex-col text-[20px] z-[1000]"
              onClick={(e) => e.stopPropagation()}
            >
                <button onClick={() => setIsOpen(false)} aria-label="Close menu" className='ml-auto'>
                  <X size={28} />
                </button>
                  <a href='/about' className='font-bold px-3 pt-6 text-left'>
                    {t('$AboutUs')}
                  </a>
                  <p className='font-bold px-3 pt-4 pb-2 text-left'>
                    {t('$AboutProduct')}
                  </p>
                  {productItems.map((p) => (
                    <button key={p.to} className="text-left rounded-lg px-3 pt-2" onClick={() => go(p.to)}>
                      <div className='flex flex-row gap-2 align-center'>
                        {p.icon}
                        {p.label}
                      </div>
                    </button>
                  ))}
              {mode === 'default' ? (
            <div className="mt-auto flex flex-col items-center gap-3 pt-4 mr-auto w-full">
                <Link
                  to={withLang(cta[0].to, currentLang)}
                  className={headerButttonDefault1}
                >
                {cta[0].label}
                </Link>
                <Link
                  to={withLang(cta[1].to, currentLang)}
                  onClick={() => setIsOpen(false)}
                  className={headerButttonDefault2}
                >
                {cta[1].label}
                </Link>
                <Suspense fallback={<div className="text-xs text-gray-500">…</div>}>
                  <LanguageSwitcher className={'text-[20px] w-full text-black bg-white rounded-2xl border-gray'} />
                </Suspense>
            </div>
          ) : (
            <div className="mt-auto flex flex-col items-center gap-3 pt-4 mr-auto w-full">
            {cta?.map((b) => (
              <Link
                to={withLang(b.to, currentLang)}
                onClick={() => setIsOpen(false)}
                className={mode === 'dark' ? headerButtonDark : headerButtonLight}
              >
                {b.label}
              </Link>
            ) 
            )}
            {bttns?.map((b) => (
              <button
                onClick={b.handleOnClick}
                className={mode === 'dark' ? headerButtonDark : headerButtonLight}
              >
                {b.label}
              </button>
            )
            )}
            <Suspense fallback={<div className="text-xs text-gray-500">…</div>}>
                  <LanguageSwitcher className={'text-[20px] w-full text-black bg-white rounded-2xl border-gray'} />
                </Suspense>
            </div>
          )}
            </aside>,
            document.body
        )}
      </div>
    </header>
  );
}
